import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { backendService } from "../services/backend.service";
import { sortJobsByDisplayName } from "../utils/jobs";
import type { Job } from "../utils/models";

/**
 * Returns the jobs enabled for the current department: jobs whose id is in
 * chosenDepartment.jobIds. If jobIds is missing or empty, returns an empty array
 * (no "show all" fallback — see branch eitan/fix-job-unauth-selectors).
 *
 * Use this for any UI that offers jobs as options (selectors, per-job grids).
 * For display-name lookups of historical job_ids that may no longer be enabled,
 * use chosenDepartment.jobs directly.
 */
export function useJobs(): Job[] {
	const { chosenDepartment } = useHospitalData();
	return useMemo(() => {
		const enabledIds = chosenDepartment.jobIds;
		// DEFAULT: a department without configured jobIds has no enabled job options to show.
		if (!enabledIds?.length) return [];
		const allowed = new Set(enabledIds);
		return sortJobsByDisplayName((chosenDepartment.jobs ?? []).filter((j) => allowed.has(j.id)));
	}, [chosenDepartment.jobs, chosenDepartment.jobIds]);
}

/**
 * Returns the full global jobs catalog (unfiltered). Use ONLY in places that must
 * pick from every job in the system — e.g. the "Enabled Jobs" picker in
 * DepartmentJobsConfig itself.
 */
export function useAllJobs(enabled?: boolean) {
	return useQuery<Job[]>({
		queryKey: ["jobs"],
		// DEFAULT: Existing callers fetch the catalog eagerly when enabled is omitted.
		enabled,
		queryFn: async () => {
			const res = await backendService.getJobs();
			return res.data;
		},
		staleTime: Number.POSITIVE_INFINITY,
	});
}

/**
 * Mutation to create a new job in the global catalog.
 * On success, invalidates the ["jobs"] query so all callers pick up the new entry.
 */
export function useCreateJob() {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { showErrorSnackbar } = useHospitalData();

	return useMutation({
		mutationFn: async (body: { displayName: string; excludeFromPpd: boolean; deptId?: string; certRoleStrings?: string[] }) => {
			track("create_job_start", { display_name: body.displayName });
			const res = await backendService.createJob(body);
			return res.data;
		},
		onSuccess: (job) => {
			track("create_job_success", { job_id: job.id, display_name: job.displayName });
			queryClient.invalidateQueries({ queryKey: ["jobs"] });
		},
		onError: (error: Error) => {
			track("create_job_error", { error_message: error.message });
			showErrorSnackbar(t("create.job.error"), {}, error);
		},
	});
}
